// src/pages/admin/AdminQuestions.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Stack, Typography, Button, Chip, Paper, CircularProgress,
  TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel,
  Collapse, IconButton, Tooltip, Divider, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Radio, RadioGroup, FormControlLabel, Checkbox,
  alpha,
} from "@mui/material";
import SearchIcon              from "@mui/icons-material/Search";
import AddIcon                 from "@mui/icons-material/Add";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ArrowUpwardIcon         from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon       from "@mui/icons-material/ArrowDownward";
import EditIcon                from "@mui/icons-material/Edit";
import DeleteIcon              from "@mui/icons-material/Delete";
import DeleteOutlineIcon       from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import RadioButtonCheckedIcon  from "@mui/icons-material/RadioButtonChecked";
import CompareArrowsIcon       from "@mui/icons-material/CompareArrows";
import AddCircleOutlineIcon    from "@mui/icons-material/AddCircleOutline";
import SaveIcon                from "@mui/icons-material/Save";
import CloseIcon               from "@mui/icons-material/Close";
import TerminalIcon            from "@mui/icons-material/Terminal";
import UploadFileIcon          from "@mui/icons-material/UploadFile";
import API          from "../../api";
import { useToast } from "../../context/ToastContext";
import { C, gradientText } from "../../theme";
import PaginationBar from "../../components/PaginationBar";
import LabEditor     from "../../components/LabEditor";

// ── Constantes ────────────────────────────────────────────────────────────────
const TYPE_META = {
  mcq:      { label: "QCM simple",      color: C.indigo[500], icon: <RadioButtonCheckedIcon sx={{ fontSize: 14 }} /> },
  multi:    { label: "Choix multiples", color: C.teal[600],   icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  match:    { label: "Association",     color: C.amber[600],  icon: <CompareArrowsIcon sx={{ fontSize: 14 }} /> },
  lab:      { label: "Lab Cisco",       color: "#7c3aed",     icon: <TerminalIcon sx={{ fontSize: 14 }} /> },
  // compat anciens types
  multiple: { label: "Choix multiples", color: C.teal[600],   icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  matching: { label: "Association",     color: C.amber[600],  icon: <CompareArrowsIcon sx={{ fontSize: 14 }} /> },
};

const EMPTY_FORM = {
  exam_id: "",
  type: "mcq",
  content: "",
  image_url: "",
  explanation: "", tags: "", difficulty: "",
  // MCQ / multi
  choices: ["", "", "", ""],
  correct_answer_index: 0,
  correct_indices: [],
  // match
  matching_pairs: [{ left: "", right: "" }],
  // lab
  lab_data: {},
};

function normalizeType(t) {
  if (t === "multiple") return "multi";
  if (t === "matching") return "match";
  return t || "mcq";
}

// ── Preview inline ────────────────────────────────────────────────────────────
function QuestionPreview({ q }) {
  const type = normalizeType(q.type || q.question_type);

  if (type === "lab") {
    const topo = q.lab_data?.topology || { devices: [], links: [] };
    const tasks = q.lab_data?.tasks || [];
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {topo.devices.map(d => (
            <Chip key={d.id} label={`${d.id} (${d.type})`} size="small"
              sx={{ background: alpha("#7c3aed", .1), color: "#7c3aed", fontWeight: 600 }} />
          ))}
        </Stack>
        <Stack spacing={.5}>
          {tasks.map((t, i) => (
            <Typography key={i} variant="caption" sx={{ color: "text.secondary" }}>
              {i + 1}. {t.desc}
            </Typography>
          ))}
        </Stack>
        {tasks.length === 0 && (
          <Typography variant="caption" color="text.secondary">Aucune tâche définie.</Typography>
        )}
      </Stack>
    );
  }

  if (type === "mcq" || type === "multi") {
    const choices = q.choices?.length
      ? q.choices
      : [q.choice_a, q.choice_b, q.choice_c, q.choice_d].filter(Boolean);
    const correctIdx   = q.correct_answer_index;
    const correctIndie = q.correct_indices || [];
    return (
      <Stack spacing={1}>
        {choices.map((c, i) => {
          const ok = type === "mcq"
            ? (correctIdx !== undefined && correctIdx !== null ? i === correctIdx : c === q.correct_answer)
            : correctIndie.includes(i);
          return (
            <Stack key={i} direction="row" spacing={1.5} alignItems="center"
              sx={{ p: 1.25, borderRadius: 2,
                border: `1px solid ${ok ? alpha(C.teal[600], .4) : C.slate[200]}`,
                background: ok ? alpha(C.teal[500], .06) : "white" }}>
              {ok
                ? <CheckCircleIcon sx={{ fontSize: 16, color: C.teal[600], flexShrink: 0 }} />
                : <Box sx={{ width: 16, height: 16, borderRadius: type === "mcq" ? "50%" : 2, border: `1.5px solid ${C.slate[300]}`, flexShrink: 0 }} />}
              <Typography variant="body2" sx={{ color: ok ? C.teal[700] : "text.primary", fontWeight: ok ? 600 : 400 }}>{c}</Typography>
              {ok && <Chip label="Correcte" size="small" sx={{ ml: "auto", height: 18, fontSize: "0.62rem", background: alpha(C.teal[500], .12), color: C.teal[700], fontWeight: 700 }} />}
            </Stack>
          );
        })}
      </Stack>
    );
  }

  if (type === "match") {
    const pairs = q.matching_pairs || [];
    return (
      <Stack spacing={1}>
        {pairs.map((pair, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <Paper sx={{ px: 2, py: 1, flexBasis: "45%", background: alpha(C.indigo[500], .06), border: `1px solid ${alpha(C.indigo[400], .3)}` }}>
              <Typography variant="body2" fontWeight={500}>{pair.left}</Typography>
            </Paper>
            <CompareArrowsIcon sx={{ color: C.slate[400], fontSize: 18 }} />
            <Paper sx={{ px: 2, py: 1, flexBasis: "45%", background: alpha(C.teal[500], .06), border: `1px solid ${alpha(C.teal[400], .3)}` }}>
              <Typography variant="body2" fontWeight={500}>{pair.right}</Typography>
            </Paper>
          </Stack>
        ))}
      </Stack>
    );
  }
  return null;
}

// ── Card question ─────────────────────────────────────────────────────────────
function QuestionCard({ q, exams, expanded, onToggle, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const type = normalizeType(q.type || q.question_type);
  const meta = TYPE_META[type] || { label: type, color: C.slate[500], icon: null };
  const examTitle = exams.find(e => e.id === q.exam_id)?.title || `Examen #${q.exam_id}`;

  return (
    <Paper sx={{ borderLeft: `4px solid ${meta.color}`, transition: "all .2s", "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,.07)" } }}>
      <Stack direction="row" alignItems="center" sx={{ p: 2, cursor: "pointer" }} onClick={onToggle} spacing={1.5}>
        <Stack spacing={0.25} onClick={e => e.stopPropagation()}>
          <Tooltip title="Monter"><span>
            <IconButton size="small" disabled={isFirst} onClick={onMoveUp} sx={{ p: 0.25, "&:disabled": { opacity: .25 } }}>
              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Descendre"><span>
            <IconButton size="small" disabled={isLast} onClick={onMoveDown} sx={{ p: 0.25, "&:disabled": { opacity: .25 } }}>
              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span></Tooltip>
        </Stack>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: .5 }}>
            <Chip size="small" icon={meta.icon} label={meta.label}
              sx={{ background: alpha(meta.color, .1), color: meta.color, fontWeight: 700, height: 22, "& .MuiChip-icon": { color: meta.color } }} />
            <Chip size="small" label={examTitle}
              sx={{ background: C.slate[100], color: C.slate[600], fontWeight: 500, height: 22 }} />
          </Stack>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: "90%" }}>
            {q.content || q.text}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center" onClick={e => e.stopPropagation()}>
          <Tooltip title="Modifier">
            <IconButton size="small" onClick={onEdit}
              sx={{ color: C.indigo[500], "&:hover": { background: alpha(C.indigo[500], .08) } }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton size="small" onClick={onDelete}
              sx={{ color: C.red[500], "&:hover": { background: alpha(C.red[500], .08) } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {expanded ? <ExpandLessIcon sx={{ color: C.slate[400], fontSize: 18 }} /> : <ExpandMoreIcon sx={{ color: C.slate[400], fontSize: 18 }} />}
        </Stack>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Divider />
        <Box sx={{ p: 2.5, background: C.slate[50] }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.5, display: "block" }}>
            Aperçu
          </Typography>
          <QuestionPreview q={q} />
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Dialog édition / création ─────────────────────────────────────────────────
function QuestionDialog({ open, onClose, onSaved, exams, initial }) {
  const toast  = useToast();
  const isEdit = Boolean(initial?.id);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [saving,  setSaving]  = useState(false);
  const [formErr, setFormErr] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const type = normalizeType(initial.type || initial.question_type);
      let choices = initial.choices?.length
        ? [...initial.choices]
        : [initial.choice_a, initial.choice_b, initial.choice_c, initial.choice_d].filter(Boolean);
      while (choices.length < 4) choices.push("");
      setForm({
        exam_id:              initial.exam_id ?? "",
        type,
        content:              initial.content || initial.text || "",
        image_url:            initial.image_url || "",
        explanation:          initial.explanation || "",
        choices,
        correct_answer_index: initial.correct_answer_index ?? 0,
        correct_indices:      initial.correct_indices || [],
        matching_pairs:       initial.matching_pairs?.length
          ? initial.matching_pairs.map(p => ({ left: p.left || "", right: p.right || "" }))
          : [{ left: "", right: "" }],
        lab_data:             initial.lab_data || {},
      });
    } else {
      setForm({ ...EMPTY_FORM, exam_id: exams[0]?.id || "", choices: ["", "", "", ""], matching_pairs: [{ left: "", right: "" }], lab_data: {} });
    }
    setFormErr("");
  }, [open, initial, exams]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Choices helpers
  const setChoice  = (i, v) => set("choices", form.choices.map((c, j) => j === i ? v : c));
  const addChoice  = () => set("choices", [...form.choices, ""]);
  const removeChoice = (i) => {
    const newC = form.choices.filter((_, j) => j !== i);
    let newIdx = form.correct_answer_index;
    if (newIdx >= newC.length) newIdx = newC.length - 1;
    const newIndie = form.correct_indices.filter(x => x !== i).map(x => x > i ? x - 1 : x);
    setForm(f => ({ ...f, choices: newC, correct_answer_index: newIdx, correct_indices: newIndie }));
  };
  const toggleCorrectIdx = (i) => {
    const prev = form.correct_indices;
    set("correct_indices", prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  };

  // Pairs helpers
  const setPair    = (i, side, v) => set("matching_pairs", form.matching_pairs.map((p, j) => j === i ? { ...p, [side]: v } : p));
  const addPair    = () => set("matching_pairs", [...form.matching_pairs, { left: "", right: "" }]);
  const removePair = (i) => set("matching_pairs", form.matching_pairs.filter((_, j) => j !== i));

  const validate = () => {
    if (!form.exam_id)        return "Choisissez un examen.";
    if (!form.content.trim()) return "La question ne peut pas être vide.";
    if (form.type === "mcq" || form.type === "multi") {
      const filled = form.choices.filter(c => c.trim());
      if (filled.length < 2) return "Ajoutez au moins 2 choix non vides.";
      if (form.type === "mcq" && (form.correct_answer_index === null || form.correct_answer_index === undefined))
        return "Choisissez la bonne réponse.";
      if (form.type === "multi" && form.correct_indices.length === 0)
        return "Cochez au moins une bonne réponse.";
    }
    if (form.type === "match") {
      const valid = form.matching_pairs.filter(p => p.left.trim() && p.right.trim());
      if (valid.length < 1) return "Ajoutez au moins une paire valide.";
    }
    if (form.type === "lab") {
      const devices = form.lab_data?.topology?.devices || [];
      const tasks   = form.lab_data?.tasks || [];
      if (devices.length === 0) return "Ajoutez au moins un équipement dans la topologie.";
      if (tasks.length === 0)   return "Ajoutez au moins une tâche à accomplir.";
    }
    return "";
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setFormErr(err); return; }
    setSaving(true); setFormErr("");

    const filledChoices = form.choices.filter(c => c.trim());
    const payload = {
      exam_id:     Number(form.exam_id),
      type:        form.type,
      content:     form.content.trim(),
      image_url:   form.image_url.trim() || null,
      explanation: form.explanation.trim() || null,
    };

    if (form.type === "mcq") {
      payload.choices               = filledChoices;
      payload.correct_answer_index  = form.correct_answer_index;
    } else if (form.type === "multi") {
      payload.choices         = filledChoices;
      payload.correct_indices = form.correct_indices;
    } else if (form.type === "match") {
      payload.matching_pairs = form.matching_pairs.filter(p => p.left.trim() && p.right.trim());
    } else if (form.type === "lab") {
      payload.lab_data = form.lab_data;
    }

    try {
      if (isEdit) {
        await API.put(`/questions/${initial.id}`, payload);
        toast.success("Question mise à jour ✓");
      } else {
        await API.post("/questions/", payload);
        toast.success("Question créée ✓");
      }
      onSaved(); onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg).join(" | ")
        : (detail || "Erreur lors de la sauvegarde.");
      setFormErr(msg);
    } finally { setSaving(false); }
  };

  const filled = form.choices.filter(c => c.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth={form.type === "lab" ? "md" : "sm"}
      fullWidth scroll="paper"
      PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        {isEdit ? "Modifier la question" : "Nouvelle question"}
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2.5 }}>
        {formErr && <Alert severity="error" sx={{ borderRadius: 2 }}>{formErr}</Alert>}

        {/* Examen + Type */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Examen *</InputLabel>
            <Select value={form.exam_id} label="Examen *" onChange={e => set("exam_id", e.target.value)}>
              {exams.map(e => <MenuItem key={e.id} value={e.id}>{e.title}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Type *</InputLabel>
            <Select value={form.type} label="Type *" onChange={e => set("type", e.target.value)}>
              <MenuItem value="mcq">QCM — réponse unique</MenuItem>
              <MenuItem value="multi">Choix multiples</MenuItem>
              <MenuItem value="match">Association</MenuItem>
              <MenuItem value="lab">
                <Stack direction="row" spacing={1} alignItems="center">
                  <TerminalIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                  <span>Lab Cisco</span>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Énoncé */}
        <TextField label="Énoncé de la question *" multiline minRows={2} fullWidth
          value={form.content} onChange={e => set("content", e.target.value)} />

        {/* Image URL — masquée pour lab */}
        {form.type !== "lab" && (
          <TextField label="URL de l'image (optionnel)" fullWidth size="small"
            value={form.image_url} onChange={e => set("image_url", e.target.value)}
            placeholder="https://…" />
        )}

        {/* Explication */}
        <TextField label="Explication (mode apprentissage, optionnel)" multiline minRows={2} fullWidth size="small"
          value={form.explanation} onChange={e => set("explanation", e.target.value)}
          placeholder="Explication affichée après validation en mode learn…" />
          {/* 3.2 Tags & difficulté */}
          <TextField label="Tags (séparés par des virgules)" fullWidth size="small"
            value={form.tags || ""}
            onChange={e => set("tags", e.target.value)}
            placeholder="réseau, sécurité, ospf, bgp…"
            helperText="Tags pour filtrage et analytics" />
          <FormControl size="small" fullWidth>
            <InputLabel>Difficulté</InputLabel>
            <Select label="Difficulté" value={form.difficulty || ""}
              onChange={e => set("difficulty", e.target.value || null)}>
              <MenuItem value="">— Non définie —</MenuItem>
              <MenuItem value={1}>😊 Facile</MenuItem>
              <MenuItem value={2}>🤔 Moyen</MenuItem>
              <MenuItem value={3}>😤 Difficile</MenuItem>
            </Select>
          </FormControl>

        {/* ── MCQ ──────────────────────────────────────────────────────── */}
        {form.type === "mcq" && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.5 }}>
              Choix — sélectionnez la bonne réponse
            </Typography>
            <RadioGroup value={String(form.correct_answer_index)}
              onChange={e => set("correct_answer_index", Number(e.target.value))}>
              <Stack spacing={1}>
                {form.choices.map((c, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}>
                    <FormControlLabel value={String(i)} control={<Radio size="small" />} label="" sx={{ mr: 0 }} />
                    <TextField size="small" fullWidth placeholder={`Choix ${i + 1}`}
                      value={c} onChange={e => setChoice(i, e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": {
                        borderColor: form.correct_answer_index === i ? C.teal[400] : undefined,
                        background:  form.correct_answer_index === i ? alpha(C.teal[500], .05) : undefined,
                      } }} />
                    {form.choices.length > 2 && (
                      <IconButton size="small" onClick={() => removeChoice(i)} sx={{ color: C.red[400], flexShrink: 0 }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            </RadioGroup>
            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addChoice} sx={{ mt: 1.5 }}>
              Ajouter un choix
            </Button>
          </Box>
        )}

        {/* ── MULTI ─────────────────────────────────────────────────────── */}
        {form.type === "multi" && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.5 }}>
              Choix — cochez toutes les bonnes réponses
            </Typography>
            <Stack spacing={1}>
              {form.choices.map((c, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={1}>
                  <Checkbox size="small" checked={form.correct_indices.includes(i)}
                    onChange={() => toggleCorrectIdx(i)}
                    sx={{ color: form.correct_indices.includes(i) ? C.teal[500] : undefined }} />
                  <TextField size="small" fullWidth placeholder={`Choix ${i + 1}`}
                    value={c} onChange={e => setChoice(i, e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": {
                      background: form.correct_indices.includes(i) ? alpha(C.teal[500], .05) : undefined,
                    } }} />
                  {form.choices.length > 2 && (
                    <IconButton size="small" onClick={() => removeChoice(i)} sx={{ color: C.red[400], flexShrink: 0 }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addChoice} sx={{ mt: 1.5 }}>
              Ajouter un choix
            </Button>
          </Box>
        )}

        {/* ── MATCH ─────────────────────────────────────────────────────── */}
        {form.type === "match" && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1.5 }}>
              Paires à associer (gauche → droite)
            </Typography>
            <Stack spacing={1.5}>
              {form.matching_pairs.map((pair, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <TextField size="small" placeholder="Élément gauche" value={pair.left}
                    onChange={e => setPair(i, "left", e.target.value)} sx={{ flex: 1 }} />
                  <CompareArrowsIcon sx={{ color: C.slate[400], flexShrink: 0 }} />
                  <TextField size="small" placeholder="Élément droite" value={pair.right}
                    onChange={e => setPair(i, "right", e.target.value)} sx={{ flex: 1 }} />
                  {form.matching_pairs.length > 1 && (
                    <IconButton size="small" onClick={() => removePair(i)} sx={{ color: C.red[400], flexShrink: 0 }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addPair} sx={{ mt: 1.5 }}>
              Ajouter une paire
            </Button>
          </Box>
        )}

        {/* ── LAB ───────────────────────────────────────────────────────── */}
        {form.type === "lab" && (
          <Box>
            {/* Bouton import JSON */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ textTransform: "none", borderRadius: "8px" }}
              >
                Importer JSON
                <input
                  type="file"
                  accept=".json,application/json"
                  hidden
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      try {
                        const parsed = JSON.parse(ev.target.result);
                        // Accepte soit un lab_data directement, soit une question complète
                        const labData = parsed.lab_data || parsed;
                        if (!labData.topology && !labData.tasks) {
                          setFormErr("JSON invalide : doit contenir 'topology' et/ou 'tasks'.");
                          return;
                        }
                        set("lab_data", labData);
                        // Pré-remplir le contenu et le scénario si présents
                        if (parsed.content && !form.content.trim())
                          set("content", parsed.content);
                        if (parsed.explanation && !form.explanation.trim())
                          set("explanation", parsed.explanation);
                        setFormErr("");
                      } catch {
                        setFormErr("Fichier JSON invalide.");
                      }
                    };
                    reader.readAsText(file);
                    // Reset l'input pour permettre re-upload du même fichier
                    e.target.value = "";
                  }}
                />
              </Button>
            </Stack>
            <LabEditor
              value={form.lab_data}
              onChange={v => set("lab_data", v)}
            />
          </Box>
        )}

        {/* Aperçu live MCQ/multi */}
        {(form.type === "mcq" || form.type === "multi") && filled.length >= 2 && (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: "0.08em", display: "block", mb: 1 }}>
              Aperçu
            </Typography>
            <Box sx={{ p: 2, borderRadius: 2, background: C.slate[50], border: `1px solid ${C.slate[200]}` }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>{form.content || "Énoncé…"}</Typography>
              <QuestionPreview q={{
                type: form.type,
                choices: filled,
                correct_answer_index: form.correct_answer_index,
                correct_indices: form.correct_indices,
              }} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>Annuler</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
          {saving ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Créer la question"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function AdminQuestions() {
  const toast = useToast();
  const [questions,  setQuestions]  = useState([]);
  const [exams,      setExams]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [examFilter, setExamFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [expanded,   setExpanded]   = useState(new Set());
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [total,      setTotal]      = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const PAGE_SIZE = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
      if (examFilter !== "all") params.set("exam_id", examFilter);
      const [qRes, eRes] = await Promise.all([
        API.get(`/questions/?${params}`),
        API.get("/exams/?page_size=100"),
      ]);
      setQuestions(qRes.data.items || []);
      setPages(qRes.data.pages || 1);
      setTotal(qRes.data.total || 0);
      setExams(Array.isArray(eRes.data) ? eRes.data : (eRes.data.items || []));
    } catch { toast.error("Impossible de charger les questions."); }
    finally { setLoading(false); }
  }, [page, examFilter]); // eslint-disable-line

  useEffect(() => { setPage(1); }, [examFilter, search, typeFilter]);
  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id) => setExpanded(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit   = (q)  => { setEditing(q);   setDialogOpen(true); };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement cette question ?")) return;
    try {
      await API.delete(`/questions/${id}`);
      toast.success("Question supprimée.");
      load();
    } catch { toast.error("Erreur lors de la suppression."); }
  };

  const moveQuestion = (id, direction) => {
    setQuestions(prev => {
      const arr = [...prev];
      const i   = arr.findIndex(q => q.id === id);
      const to  = i + direction;
      if (to < 0 || to >= arr.length) return prev;
      [arr[i], arr[to]] = [arr[to], arr[i]];
      return arr;
    });
  };

  const filtered = questions.filter(q => {
    const type = normalizeType(q.type || q.question_type);
    const matchType   = typeFilter === "all" || type === typeFilter;
    const matchSearch = !search.trim()
      || (q.content || q.text || "").toLowerCase().includes(search.trim().toLowerCase());
    return matchType && matchSearch;
  });

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
        alignItems={{ sm: "center" }} sx={{ mb: 4 }} spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">GESTION</Typography>
          <Typography variant="h4" fontWeight={700}>
            <Box component="span" sx={gradientText}>Questions</Box>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {total} question{total !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nouvelle question
        </Button>
      </Stack>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField placeholder="Rechercher une question…"
            value={search} onChange={e => setSearch(e.target.value)} sx={{ flexGrow: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.secondary" }} /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Examen</InputLabel>
            <Select value={examFilter} label="Examen" onChange={e => setExamFilter(e.target.value)}>
              <MenuItem value="all">Tous les examens</MenuItem>
              {exams.map(e => <MenuItem key={e.id} value={e.id}>{e.title}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value)}>
              <MenuItem value="all">Tous les types</MenuItem>
              <MenuItem value="mcq">QCM simple</MenuItem>
              <MenuItem value="multi">Choix multiples</MenuItem>
              <MenuItem value="match">Association</MenuItem>
              <MenuItem value="lab">Lab Cisco</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" size="small"
            onClick={() => { setSearch(""); setExamFilter("all"); setTypeFilter("all"); }}>
            Réinitialiser
          </Button>
        </Stack>
      </Paper>

      {/* Liste */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Aucune question trouvée.</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Créer une question
          </Button>
        </Box>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {filtered.map((q, i) => (
            <QuestionCard key={q.id}
              q={q} exams={exams}
              expanded={expanded.has(q.id)}
              onToggle={() => toggleExpand(q.id)}
              onEdit={() => openEdit(q)}
              onDelete={() => handleDelete(q.id)}
              onMoveUp={() => moveQuestion(q.id, -1)}
              onMoveDown={() => moveQuestion(q.id, 1)}
              isFirst={i === 0}
              isLast={i === filtered.length - 1}
            />
          ))}
        </Stack>
      )}

      <PaginationBar page={page} pages={pages} total={total} pageSize={PAGE_SIZE}
        onPageChange={setPage} onPageSizeChange={() => {}} pageSizeOptions={[15]}
        loading={loading} />

      <QuestionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        exams={exams}
        initial={editing}
      />
    </Box>
  );
}